import { z } from "zod";
import { AgencyIdSchema, ProjectIdSchema } from "../primitives/ids.js";
import { ExternalReadinessClientSafeSummaryV1Schema } from "./external-readiness-report.js";
import { ExternalProofFixKitClientSafeSummaryV1Schema } from "./external-proof-fix-kit.js";
import { ExternalProofSalesRoomClientSafeViewV1Schema } from "./external-proof-sales-room.js";
import { ExternalAfterFixProofClientSafeSummaryV1Schema } from "./external-after-fix-proof.js";
import { ClientSafeWebsiteOwnershipViewV1Schema } from "./website-ownership-record.js";
import { ExternalReadinessInternalViewV1Schema } from "./external-readiness-report.js";
import { ExternalProofFixKitInternalViewV1Schema } from "./external-proof-fix-kit.js";
import { ExternalProofSalesRoomInternalViewV1Schema } from "./external-proof-sales-room.js";
export const PROJECT_RECORD_CENTER_VIEW_VERSION = "0.1";
export const ProjectRecordCenterPhaseSchema = z.enum([
    "intake",
    "scan",
    "import",
    "confirmation",
    "readiness",
    "fix_kit",
    "sales_room",
    "approval",
    "change_request",
    "after_fix",
    "handoff",
    "audit",
]);
export const ProjectRecordTimelineEntryStatusSchema = z.enum([
    "complete",
    "pending",
    "blocked",
    "skipped",
]);
export const ProjectRecordTimelineEntryV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.timelineEntry.v1"),
    entryId: z.string().min(1),
    occurredAt: z.string().datetime(),
    phase: ProjectRecordCenterPhaseSchema,
    headline: z.string().min(1).max(200),
    summary: z.string().min(1).max(2000),
    status: ProjectRecordTimelineEntryStatusSchema,
})
    .strict();
export const ProjectRecordDocumentTypeSchema = z.enum([
    "readiness",
    "fix_kit",
    "sales_room",
    "after_fix",
    "ownership",
]);
export const ProjectRecordDocumentV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.document.v1"),
    docType: ProjectRecordDocumentTypeSchema,
    label: z.string().min(1).max(200),
    lifecycleStatus: z.string().min(1).max(100),
    clientSafeRef: z.string().min(1).max(200).optional(),
    available: z.boolean(),
})
    .strict();
export const ProjectRecordHandoffSummaryV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.handoffSummary.v1"),
    ownershipStatus: z.string().min(1).max(200),
    riskScoreLabel: z.string().min(1).max(50).optional(),
    clientSummary: z.string().min(1).max(2000),
    handoffAvailable: z.boolean(),
})
    .strict();
export const ProjectRecordWarningV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.warning.v1"),
    code: z.string().min(1).max(100),
    message: z.string().min(1).max(500),
    severity: z.enum(["info", "warning"]),
})
    .strict();
export const ProjectRecordCenterSummaryV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.summary.v1"),
    projectId: ProjectIdSchema,
    projectMode: z.string().min(1),
    currentStatus: z.string().min(1).max(100),
    nextAction: z.string().min(1).max(500).optional(),
    clientSafe: z.boolean(),
    generatedAt: z.string().datetime(),
})
    .strict();
export const ProjectRecordCenterViewV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.view.v1"),
    viewVersion: z.literal(PROJECT_RECORD_CENTER_VIEW_VERSION),
    summary: ProjectRecordCenterSummaryV1Schema,
    timeline: z.array(ProjectRecordTimelineEntryV1Schema),
    documents: z.array(ProjectRecordDocumentV1Schema),
    handoff: ProjectRecordHandoffSummaryV1Schema.optional(),
    warnings: z.array(ProjectRecordWarningV1Schema).optional(),
})
    .strict();
export const ProjectRecordCenterAgencySectionsV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.agencySections.v1"),
    attachedUrl: z.string().url().optional(),
    scanStatus: z.string().min(1).optional(),
    importReviewStatus: z.string().min(1).optional(),
    importDecisionCount: z.number().int().min(0).optional(),
    hasBusinessGraphRevision: z.boolean().optional(),
    readiness: ExternalReadinessInternalViewV1Schema.optional(),
    fixKit: ExternalProofFixKitInternalViewV1Schema.optional(),
    salesRoom: ExternalProofSalesRoomInternalViewV1Schema.optional(),
})
    .strict();
export const ProjectRecordCenterAgencyViewV1Schema = ProjectRecordCenterViewV1Schema.extend({
    summary: ProjectRecordCenterSummaryV1Schema.extend({
        clientSafe: z.literal(false),
        agencyId: AgencyIdSchema,
    }),
    sections: ProjectRecordCenterAgencySectionsV1Schema.optional(),
}).strict();
export const ProjectRecordCenterClientSectionsV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.clientSections.v1"),
    readiness: ExternalReadinessClientSafeSummaryV1Schema.optional(),
    fixKit: ExternalProofFixKitClientSafeSummaryV1Schema.optional(),
    salesRoom: ExternalProofSalesRoomClientSafeViewV1Schema.optional(),
    afterFix: ExternalAfterFixProofClientSafeSummaryV1Schema.optional(),
    ownership: ClientSafeWebsiteOwnershipViewV1Schema.optional(),
})
    .strict();
export const ProjectRecordCenterClientViewV1Schema = z
    .object({
    schemaId: z.literal("projectRecordCenter.clientView.v1"),
    viewVersion: z.literal(PROJECT_RECORD_CENTER_VIEW_VERSION),
    summary: z
        .object({
        schemaId: z.literal("projectRecordCenter.clientSummary.v1"),
        projectMode: z.string().min(1),
        currentStatus: z.string().min(1).max(100),
        nextAction: z.string().min(1).max(500).optional(),
        clientSafe: z.literal(true),
        generatedAt: z.string().datetime(),
    })
        .strict(),
    timeline: z.array(ProjectRecordTimelineEntryV1Schema),
    documents: z.array(ProjectRecordDocumentV1Schema),
    handoff: ProjectRecordHandoffSummaryV1Schema.optional(),
    warnings: z.array(ProjectRecordWarningV1Schema).optional(),
    sections: ProjectRecordCenterClientSectionsV1Schema.optional(),
})
    .strict();
//# sourceMappingURL=project-record-center.js.map